import { AfterViewInit, Component, Inject, inject, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take, tap } from 'rxjs';
import { IAllStudentsByPermissionRequest, IViewClassStudentsRequest } from '@core/interfaces/iclasses';
import { ITableColumn } from '@core/interfaces/itable';
import { TableColumnType } from '@core/enums/table';
import { setIconDisplayObject } from '@core/services/helper.service';
import { ClassService } from '@core/services/class.service';
import { IViewStudentByClassModalData } from '@views/user/reports';
import { AgencyService } from '@core/services/agency.service';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { IAgency } from '@core/interfaces/iagency';
import { TranslocoService } from '@jsverse/transloco';

interface ViewStudentsByClassReportData {
  isSelected: boolean;
  firstName: string;
  lastName: string;
  class: string;
  ratingPeriod: string;
  classEnrollmentDate: Date;
  classExitDate?: Date;
  daysEnrolled: number;
  isLocked: boolean;
  id: number;
  studentClassEnrollmentId: number;
}

@Component({
    selector: 'drdp-view-student-by-class-modal',
    templateUrl: './view-student-by-class-modal.component.html',
    styleUrls: ['./view-student-by-class-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewStudentByClassModalComponent implements OnInit, AfterViewInit {
  private selectedEnrollmentIds: number[] = [];
  agencyIds?: number[];
  siteId?: number;
  classId?: number;
  globalRatingPeriodId?: number;
  private readonly ageGradeInstrumentId: number;
  private readonly ratingPeriod: string;
  private readonly onlyLocked: boolean;
  private readonly onlyActive: boolean;

  canSelectAll: boolean;
  classroomName?: string;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  tableData!: ViewStudentsByClassReportData[];
  pageSizeOptions: number[] = [10, 25, 50, 100];
  totalData: number = 0;
  pageSize: number = 10;
  pageIndex: number = 1;
  isSelectAll = false;
  selectAllPagesSet: Set<number> = new Set<number>();
  is2025: boolean = false;
  tableColumns: ITableColumn[] = [];

  classService: ClassService = inject(ClassService);
  agencyService = inject(AgencyService);
  authService = inject(AuthService);
  permissionService = inject(PermissionService);
  private translocoService = inject(TranslocoService);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  hasAccessToAssignedChildren = this.permissionService.checkPermission(
    Permission.AccessToAssignedChildrenReporting
  );

  constructor(public dialogRef: MatDialogRef<ViewStudentByClassModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    this.ageGradeInstrumentId = data.ageGradeInstrumentId;
    this.ratingPeriod = data.ratingPeriod;
    this.classroomName = data.classroomName;
    this.onlyLocked = data.onlyLocked;
    this.onlyActive = data.onlyActive;
    this.canSelectAll = data.canSelectAll ?? false;
    this.is2025 = data.is2025 ?? false;
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.buildTableColumns();
    this.preparePayload();
  }

  buildTableColumns(): void {
    this.tableColumns = [
      {
        columnDef: 'isSelected',
        header: this.translocoService.translate('reports.tableColumns.select'),
        type: TableColumnType.checkBox,
        checkClass: 'checkbox'
      },
      {
        columnDef: 'firstName',
        header: this.translocoService.translate('reports.tableColumns.firstName'),
        type: TableColumnType.text,
        isSortable: true
      },
      {
        columnDef: 'lastName',
        header: this.translocoService.translate('reports.tableColumns.lastName'),
        type: TableColumnType.text,
        isSortable: true
      },
      {
        columnDef: 'ageGradeInstrumentName',
        header: this.translocoService.translate('reports.tableColumns.ageGroup'),
        type: TableColumnType.text,
        isSortable: false
      },
      {
        columnDef: 'class',
        header: this.translocoService.translate('reports.tableColumns.class'),
        type: TableColumnType.text,
        isSortable: true
      },
      {
        columnDef: 'ratingPeriod',
        header: this.translocoService.translate('reports.tableColumns.ratingPeriod'),
        type: TableColumnType.text,
        isSortable: true
      },
      {
        columnDef: 'classEnrollmentDate',
        header: this.translocoService.translate('reports.tableColumns.classroomStartDate'),
        type: TableColumnType.date,
        isSortable: true
      },
      {
        columnDef: 'classExitDate',
        header: this.translocoService.translate('reports.tableColumns.classroomEndDate'),
        type: TableColumnType.date,
        isSortable: true
      },
      {
        columnDef: 'daysEnrolled',
        header: this.translocoService.translate('reports.tableColumns.daysEnrolled'),
        type: TableColumnType.text,
        isSortable: true
      },
      {
        columnDef: 'isLocked',
        header: this.translocoService.translate('reports.tableColumns.isLocked'),
        type: TableColumnType.boolIcon,
        truthyClass: 'text-drdpGreen-300',
        truthyIconUrl: 'assets/images/misc/lock-alt.svg',
        falsyClass: 'text-drdpgrey-200',
        falsyIconUrl: 'assets/images/misc/padlock.svg',
        isSortable: true
      }
    ];
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.paginator._intl.itemsPerPageLabel = '';
  }

  isStudentSelected(): boolean {
    return this.selectedEnrollmentIds.length > 0
  }

  onToggleSwitchActionItem(event: any): void {
    const studentIndex = this.selectedEnrollmentIds.findIndex(cs => cs === event.data.studentClassEnrollmentId);
    if (studentIndex === -1) {
      this.selectedEnrollmentIds.push(event.data.studentClassEnrollmentId);
    } else {
      this.selectedEnrollmentIds.splice(studentIndex, 1);
    }
  }

  selectStudents(): void {
    const selectedEnrollmentIds = this.selectedEnrollmentIds.map(cs => cs);
    const selectedStudentIds = Array.from(
      new Set(this.tableData
        .filter(student =>  this.selectedEnrollmentIds.includes(student.studentClassEnrollmentId))
        .map(student => student.id))
    );
    this.clearSelectedStudents();
    this.dialogRef.close({ selectedStudentIds: selectedStudentIds, selectedStudentEnrollmentIds: selectedEnrollmentIds });
  }

  selectAllStudents(): void {
    this.isSelectAll = !this.isSelectAll;

    if (this.tableData) {
      this.tableData.forEach((student: any) => {
        student.isSelected = this.isSelectAll;
      });

      if (this.isSelectAll) {
        this.tableData.forEach((student: any) => {
          const index = this.selectedEnrollmentIds.findIndex(
            (sce: any) => sce === student.studentClassEnrollmentId
          );
          if (index === -1) {
            this.selectedEnrollmentIds.push(student.studentClassEnrollmentId);
          }
          this.selectAllPagesSet.add(this.paginator.pageIndex);
        });
      } else {
        this.selectedEnrollmentIds = this.selectedEnrollmentIds.filter((sce: any) =>
          !this.tableData.some((tb: any) => tb.studentClassEnrollmentId == sce)
        );
      }
    }
  }

  getLinkedAgencyIds(): any {
    const user = this.authService.getCurrentUser();
    this.agencyService.getLinkedAgencies(user.userId).pipe(take(1)).subscribe((res: IAgency[]) => {
      if (res) {
        this.agencyIds =  res?.map((s: any) => s.id);
        this.getClassStudents()
      }
    })
  }

  preparePayload() {
    this.classId = this.data.classId;
    this.siteId = this.data.siteId;
    this.globalRatingPeriodId = this.data.globalRatingPeriodId;
    if (this.data.agencyIds == null) {
      this.getLinkedAgencyIds();
      return;
    }
    if (this.data.agencyIds) this.agencyIds = [this.data.agencyIds];
    this.getClassStudents();
  }

  private clearSelectedStudents(): void {
    this.selectedEnrollmentIds = [];
  };

  private getClassStudents(): void {
    let payload: IAllStudentsByPermissionRequest;
    payload = {
      ageGradeInstrumentId: this.ageGradeInstrumentId,
      classId: this.classId, 
      onlyActive: this.onlyActive,
      onlyLocked: this.onlyLocked,
      pageIndex: this.pageIndex,
      agencyIds: this.agencyIds,
      globalRatingPeriodId: this.globalRatingPeriodId,
      siteId: this.siteId,
      is2025: this.is2025
    };

    this.classService.getAllClassStudentsByPermissions(payload)
    .pipe(
      take(1),
      tap((res: any) => {
        if (res) {
          res.items = res.items.map((data: any) => {
            const studentIndex = this.selectedEnrollmentIds.findIndex(cs => cs === data.studentClassEnrollmentId);
            const daysEnrolledTicks = !!data.classExitDate
              ? new Date(data.classExitDate).getTime() - new Date(data.classEnrollmentDate).getTime()
              : (Date.now() - new Date(data.classEnrollmentDate).getTime());
            return {
              ...data,
              isSelected: studentIndex !== -1,
              class: data.classroomName,
              ratingPeriod: this.ratingPeriod,
              daysEnrolled: Math.round(daysEnrolledTicks / (1000 * 60 * 60 * 24)),
              displayIcons: setIconDisplayObject(this.tableColumns, data),
            }
          })
          this.dataSource.data = res.items;
          this.tableData = res.items;
          this.totalData = res.totalData;
        }
      })
    )
    .subscribe();
  }
}
